'use client';
import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CTAButton } from '../common/cta-button';

import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useFormState, useFormStatus } from 'react-dom';
import { SpinnerIcon } from '../assets/icons';

// The `useFormStatus` hook is required to be a child component of the form in order for it to update accordingly
// https://unicorn-utterances.com/posts/what-is-use-form-state-and-status#What-is-useFormStatus
function SubmitButton() {
  const { pending } = useFormStatus();

  return (
    <CTAButton type="submit" disabled={pending}>
      {pending ? <SpinnerIcon /> : 'Send message'}
    </CTAButton>
  );
}

interface InitialStateOptions {
  error?: Record<string, string | null>;
  success?: boolean;
}

const initialState: InitialStateOptions = {
  success: false,
  error: {
    firstName: null,
    lastName: null,
    email: null,
    companyName: null,
    jobTitle: null,
    message: null,
  },
} as const;

export const FormContainer = ({
  submitRequest,
}: {
  submitRequest: (prevState: InitialStateOptions, formData: FormData) => Promise<InitialStateOptions>;
}) => {
  const { pending } = useFormStatus();
  const [state, formAction] = useFormState(submitRequest, initialState);

  if (state.success) {
    return (
      <div className="mt-20 max-w-2xl p-8 md:mx-auto md:px-4">
        <h3 className="mb-2 text-lg font-semibold leading-normal">Thanks for reaching out!</h3>
        <p className="text-muted-foreground mb-2 leading-normal">
          We look forward to getting in touch very soon, but until then feel free to have a look around!
        </p>
      </div>
    );
  }

  return (
    <form action={formAction as unknown as string} className="px-6 pb-24 pt-20 sm:pb-32 lg:px-8 lg:py-20">
      <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
        <div>
          <Label className="flex gap-3" htmlFor="first-name">
            First name *
            {state.error?.firstName && <span className="text-xs text-red-400">{state.error.firstName}</span>}
          </Label>

          <Input
            className="mt-2.5"
            type="text"
            name="first-name"
            id="first-name"
            autoComplete="given-name"
            aria-required={true}
            aria-invalid={Boolean(state.error?.firstName)}
          />
        </div>
        <div>
          <Label className="flex gap-3" htmlFor="last-name">
            Last name * {state.error?.lastName && <span className="text-xs text-red-400">{state.error.lastName}</span>}
          </Label>

          <Input
            className="mt-2.5"
            type="text"
            name="last-name"
            id="last-name"
            autoComplete="family-name"
            aria-required={true}
            aria-invalid={Boolean(state.error?.lastName)}
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="flex gap-3" htmlFor="email">
            Email *{state.error?.email && <span className="text-xs text-red-400">{state.error.email}</span>}
          </Label>

          <Input
            className="mt-2.5"
            type="email"
            name="email"
            id="email"
            autoComplete="email"
            aria-required={true}
            aria-invalid={Boolean(state.error?.email)}
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="flex gap-3" htmlFor="company-name">
            Company name *
            {state.error?.companyName && <span className="text-xs text-red-400">{state.error.companyName}</span>}
          </Label>

          <Input
            className="mt-2.5"
            type="text"
            name="company-name"
            id="company-name"
            autoComplete="text"
            aria-required={true}
            aria-invalid={Boolean(state.error?.companyName)}
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="flex gap-3" htmlFor="job-title">
            Job title *{state.error?.jobTitle && <span className="text-xs text-red-400">{state.error.jobTitle}</span>}
          </Label>

          <Input
            className="mt-2.5"
            type="text"
            name="job-title"
            id="job-title"
            autoComplete="text"
            aria-required={true}
            aria-invalid={Boolean(state.error?.jobTitle)}
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="flex gap-3" htmlFor="message">
            Describe your use case and expected traffic *
            {state.error?.message && <span className="text-xs text-red-400">{state.error.message}</span>}
          </Label>

          <Textarea
            className="mt-2.5"
            name="message"
            id="message"
            rows={4}
            defaultValue={''}
            aria-required={true}
            aria-invalid={Boolean(state.error?.message)}
          />
        </div>
        <div className="sm:col-span-2">
          <label
            htmlFor="nature-of-enquiry"
            className="align-start text-sm text-sm font-semibold leading-6 text-gray-900 dark:text-gray-300"
          >
            Nature of enquiry
          </label>
          <div className="mt-2.5">
            <Select>
              <SelectTrigger>
                <SelectValue placeholder="Please Select" className="" />
              </SelectTrigger>
              <SelectContent position="popper" className="inter-var bg-white dark:border-zinc-900 dark:bg-zinc-800">
                <SelectItem className="cursor-pointer hover:dark:bg-zinc-700 focus:dark:bg-zinc-700" value="select">
                  Sales Support
                </SelectItem>
                <SelectItem
                  className="cursor-pointer hover:dark:bg-zinc-700 focus:dark:bg-zinc-700"
                  value="billing-account"
                >
                  Billing / Account Support
                </SelectItem>
                <SelectItem className="cursor-pointer hover:dark:bg-zinc-700 focus:dark:bg-zinc-700" value="careers">
                  Careers
                </SelectItem>
                <SelectItem className="cursor-pointer hover:dark:bg-zinc-700 focus:dark:bg-zinc-700" value="compliance">
                  Compliance
                </SelectItem>
                <SelectItem className="cursor-pointer hover:dark:bg-zinc-700 focus:dark:bg-zinc-700" value="technical">
                  Technical
                </SelectItem>
                <SelectItem className="cursor-pointer hover:dark:bg-zinc-700 focus:dark:bg-zinc-700" value="other">
                  Other
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <div className="mt-8 flex justify-end">
        <SubmitButton />
      </div>
    </form>
  );
};
